/* eslint-disable import/prefer-default-export */
import { TFunction } from 'i18next';
import flatMap from 'lodash/flatMap';
import {
  compareByLevelAsc,
  TAXONOMIES_PRIORITIES,
} from 'stores/Catalog/config';
import {
  prioritizeEntryField,
  FilterConfiguration,
} from 'stores/common/Filters';
import { LearningPath, VocabularyItem, VocabularyNamespaces } from 'types';

/**
 * This function dedupes the lp.levels by their vocabulary label.
 *
 * ----------
 *
 * Example:
 *
 * For a vocabulary like this:
 *
 * [
 *   { token: '4', display_name: 'Intermediate' },
 *   { token: '5', display_name: 'Intermediate' },
 *   { token: '6', display_name: 'Intermediate' },
 *   { token: '7', display_name: 'Intermediate' },
 *   { token: '8', display_name: 'Advanced' },
 * ]
 *
 * And a skills path levels list like this:
 * [4, 5, 6, 8]
 * It will return: ['6', '8']
 * Ignoring unnecessary values like '4', '5', '6' and '7' because they have the same label 'Intermediate'.
 * This is needed because the filter engine "groupIdenticalLabels" was only designed to work with single values.
 */
const dedupeLevelsByLabel = (
  levels: number[] | undefined,
  vocabulary: VocabularyItem[],
): string[] => {
  if (!levels?.length) return [];

  const labelByToken = new Map(
    vocabulary.map(({ token, display_name }) => [token, display_name]),
  );
  const seenLabels = new Set<string>();

  return levels.reduce<string[]>((deduped, level) => {
    const token = `${level}`;
    const label = labelByToken.get(token) ?? token;

    if (seenLabels.has(label)) return deduped;

    seenLabels.add(label);
    deduped.push(token);
    return deduped;
  }, []);
};

/**
 * Returns the filter configurations for the lp catalog.
 * @returns The filter configurations.
 */
export const getFilterConfigurations = (
  t: TFunction = (str: string) => str,
): FilterConfiguration<LearningPath>[] => {
  const rolesFilterConfig: FilterConfiguration<LearningPath> = {
    key: 'roles',
    title: t('Job roles'),
    extractFilterValues: (entries) => flatMap(entries, 'job_roles'),
    vocabularyNamespace: VocabularyNamespaces.JobRoles,
    validateEntry: (entry: LearningPath, checkedRoles) => {
      const { job_roles: jobRoles } = entry;
      return jobRoles?.some((item) => checkedRoles.includes(item));
    },
  };

  const filterConfigurations: FilterConfiguration<LearningPath>[] = [
    rolesFilterConfig,
  ];

  filterConfigurations.push(
    {
      key: 'taxonomyProducts',
      title: t('Products'),
      extractFilterValues: (entries) =>
        entries
          .map((entry) =>
            prioritizeEntryField<LearningPath>(
              entry,
              TAXONOMIES_PRIORITIES.products,
              true,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingTaxonomyProducts,
      validateEntry: (entry, checkedTaxonomyProducts) => {
        const entryProducts: string[] = prioritizeEntryField<LearningPath>(
          entry,
          TAXONOMIES_PRIORITIES.products,
          true,
        );

        return entryProducts?.some((item) =>
          checkedTaxonomyProducts.includes(item),
        );
      },
    },
    {
      key: 'taxonomyTopics',
      title: t('Topics'),
      extractFilterValues: (entries) =>
        entries
          .map((entry) =>
            prioritizeEntryField<LearningPath>(
              entry,
              TAXONOMIES_PRIORITIES.topics,
              true,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingTaxonomyTopics,
      validateEntry: (entry, checkedTaxonomyTopics) => {
        const entryTopics: string[] = prioritizeEntryField<LearningPath>(
          entry,
          TAXONOMIES_PRIORITIES.topics,
          true,
        );

        return entryTopics?.some((item) =>
          checkedTaxonomyTopics.includes(item),
        );
      },
    },
    {
      key: 'taxonomyAudiences',
      title: t('Audiences'),
      extractFilterValues: (entries) =>
        entries
          .map((entry) =>
            prioritizeEntryField<LearningPath>(
              entry,
              TAXONOMIES_PRIORITIES.audiences,
              true,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingTaxonomyAudiences,
      validateEntry: (entry, checkedTaxonomyAudiences) => {
        const entryAudiences: string[] = prioritizeEntryField<LearningPath>(
          entry,
          TAXONOMIES_PRIORITIES.audiences,
          true,
        );

        return entryAudiences?.some((item) =>
          checkedTaxonomyAudiences.includes(item),
        );
      },
    },
    {
      key: 'levels',
      title: t('Levels'),
      groupIdenticalLabels: true,
      extractFilterValues: (entries, vocabulary) =>
        entries.flatMap((entry) =>
          dedupeLevelsByLabel(entry.levels, vocabulary),
        ),
      vocabularyNamespace: VocabularyNamespaces.OfferingLevel,
      compareFn: compareByLevelAsc,
      validateEntry: (entry, checkedLevels) =>
        entry.levels?.some((level) => checkedLevels.includes(`${level}`)),
    },
  );

  filterConfigurations.push({
    key: 'pathTypes',
    title: t('Path types'),
    extractFilterValues: (entries) => flatMap(entries, 'path_type'),
    vocabularyNamespace: VocabularyNamespaces.SkillsPathTypes,
    validateEntry: (entry, checkedPathTypes) =>
      checkedPathTypes?.some((item) => item === entry.path_type),
    compareFn: (a) => (a.key === 'custom' ? -1 : 1), // custom path should be first
  });

  return filterConfigurations;
};
